import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Play } from "@phosphor-icons/react";
import { useProgressStore } from "../../app/stores/progressStore";
import { useThemeStore, type LanguageMode } from "../../app/stores/themeStore";
import {
  getCourse,
  getUnits,
  type CourseId,
  type Unit,
} from "../engine/lessonRegistry";
import { lessonPath } from "../../app/routes";
import { Button } from "../../app/components/ui/Button";
import styles from "./HomeView.module.css";

const FALLBACK_SNIPPET: Record<LanguageMode, string> = {
  js: `move();
turnLeft();`,
  python: `move()
turn_left()`,
};

function sampleCodeSnippet(unit: Unit, language: LanguageMode): string {
  return unit.snippets?.[language] ?? FALLBACK_SNIPPET[language];
}

interface CourseViewProps {
  course: CourseId;
}

export function CourseView({ course }: CourseViewProps) {
  const navigate = useNavigate();
  const languageMode = useThemeStore((s) => s.languageMode);
  const setActiveCourse = useProgressStore((s) => s.setActiveCourse);
  const progress = useProgressStore((s) => s.courses[course]);
  const registry = getUnits(course);
  const info = getCourse(course);

  useEffect(() => {
    setActiveCourse(course);
  }, [course, setActiveCourse]);

  const { hasStarted, currentUnit, currentLesson, units } = progress;

  const totalLessons = registry.reduce(
    (sum, unit) => sum + unit.lessons.length,
    0,
  );
  const completedLessons = units.reduce(
    (sum, unit) => sum + unit.statuses.filter((s) => s === "finished").length,
    0,
  );
  const overallProgress =
    totalLessons > 0 ? completedLessons / totalLessons : 0;

  const resumeUnit = hasStarted ? currentUnit : 1;
  const resumeLesson = hasStarted ? currentLesson : 1;
  const resumeLabel = hasStarted
    ? `Continue unit ${resumeUnit}, lesson ${resumeLesson}`
    : "Start learning";

  return (
    <main className={styles.home}>
      <section className={styles.hero} aria-labelledby="course-title">
        <Button
          variant="secondary"
          className={styles.back}
          icon={<ArrowLeft size={16} />}
          onClick={() => navigate("/")}
        >
          Courses
        </Button>
        <h1 id="course-title" className={styles.courseTitle}>
          {info.name}
        </h1>
        <p className={styles.tagline}>{info.description}</p>

        <div className={styles.progress} aria-label="Course progress">
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${Math.round(overallProgress * 100)}%` }}
            />
          </div>
          <span className={styles.progressText}>
            {completedLessons} of {totalLessons} lessons completed
          </span>
        </div>

        <Button
          variant="primary"
          className={styles.start}
          icon={
            hasStarted ? (
              <ArrowRight size={18} />
            ) : (
              <Play weight="fill" size={18} />
            )
          }
          iconPosition={hasStarted ? "end" : "start"}
          onClick={() => navigate(lessonPath(course, resumeUnit, resumeLesson))}
        >
          {resumeLabel}
        </Button>
      </section>

      <section className={styles.unitsSection} aria-label="Units">
        <div className={styles.units} role="list">
          {registry.map((unit, index) => {
            const unitIndex = index + 1;
            const statuses = units[index]?.statuses ?? [];
            const total = unit.lessons.length;
            const finishedCount = statuses.filter(
              (s) => s === "finished",
            ).length;
            const allFinished = total > 0 && finishedCount === total;
            const started = statuses.some((s) => s !== "notStarted");
            const progressValue = total > 0 ? finishedCount / total : 0;

            return (
              <button
                key={unit.name}
                type="button"
                className={[
                  styles.unit,
                  allFinished ? styles.finished : "",
                  started && !allFinished ? styles.started : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                role="listitem"
                aria-label={`Unit ${unitIndex}: ${unit.name}. ${finishedCount} of ${total} lessons completed`}
                title={unit.name}
                onClick={() => navigate(lessonPath(course, unitIndex, 1))}
              >
                <div className={styles.unitHeader}>
                  <div className={styles.unitTitleGroup}>
                    <span className={styles.unitNumber}>Unit {unitIndex}</span>
                    <span className={styles.unitName}>{unit.name}</span>
                  </div>
                  {allFinished ? (
                    <Check
                      className={styles.statusIcon}
                      size={20}
                      weight="bold"
                      aria-hidden="true"
                    />
                  ) : started ? (
                    <Play
                      className={styles.statusIcon}
                      size={20}
                      weight="fill"
                      aria-hidden="true"
                    />
                  ) : null}
                </div>

                <div className={styles.codePreview} aria-hidden="true">
                  <pre className={styles.codeBlock}>
                    <code>{sampleCodeSnippet(unit, languageMode)}</code>
                  </pre>
                </div>

                <div className={styles.unitProgress}>
                  <div className={styles.unitProgressTrack}>
                    <div
                      className={styles.unitProgressFill}
                      style={{ width: `${Math.round(progressValue * 100)}%` }}
                    />
                  </div>
                  <span className={styles.unitProgressText}>
                    {finishedCount}/{total}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>
    </main>
  );
}
